/**
 * Product pulse — vista ejecutiva para responsables de producto.
 * RPC: get_product_pulse_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';

export interface ProductKpi {
    key: string;
    label: string;
    category: string;
    current: number;
    previous: number;
    change_pct: number;
}

export interface ProductAlert {
    severity: 'warning' | 'info';
    title: string;
    body: string;
}

export interface ActivationChain {
    registered: number;
    onboarding_v2_completed: number;
    profile_completed: number;
    first_capture: number;
    third_capture: number;
}

export interface FeatureMomentum {
    key: string;
    label: string;
    current: number;
    previous: number;
    change_pct: number;
}

export interface ProductPulseTrendPoint {
    date: string;
    value: number;
}

export interface ProductPulseTrends7d {
    registrations: ProductPulseTrendPoint[];
    active_users: ProductPulseTrendPoint[];
    captures: ProductPulseTrendPoint[];
}

export interface ProductPulseQuarter {
    label: string;
    start_date: string;
    end_date: string;
    days_elapsed: number;
    days_total: number;
}

export interface ProductPulseQuarterToDate {
    registrations: number;
    active_users: number;
    captures: number;
    onboarding_v2_completed: number;
    store_orders: number;
}

export interface ProductPulseAnalytics {
    period_days: number;
    comparison_label: string;
    alerts: ProductAlert[];
    kpis: ProductKpi[];
    activation_chain: ActivationChain;
    onboarding_insights: {
        v2_completion_rate: number;
        worst_v2_step: { label: string; drop_pct: number } | null;
        worst_profile_step: { step: string; drop_pct: number } | null;
        location_grant_rate: number | null;
    };
    feature_momentum: FeatureMomentum[];
    trends_7d?: ProductPulseTrends7d;
    quarter?: ProductPulseQuarter;
    quarter_to_date?: ProductPulseQuarterToDate;
}

export function useProductPulse(days: number = 30) {
    return useQuery<ProductPulseAnalytics>({
        queryKey: ['admin', 'product-pulse', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_product_pulse_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const parsed = parseProductPulseResponse(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_product_pulse_analytics');
            }
            return parsed;
        },
        retry: 1,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export const KPI_CATEGORY_LABELS: Record<string, string> = {
    growth: 'Crecimiento',
    engagement: 'Engagement',
    core: 'Core',
    activation: 'Activación',
    monetization: 'Monetización',
    intel: 'Intel / IA',
    gamification: 'Gamificación',
    virality: 'Viralidad',
};

/** Porcentaje de la cohorte. Null si el paso no es subconjunto (evitar >100%). */
export function cohortStepPct(current: number, cohort: number): number | null {
    if (cohort <= 0 || current < 0 || current > cohort) return null;
    return Math.round((current / cohort) * 100);
}

export function activationStepRate(current: number, previous: number): number {
    if (previous <= 0) return 0;
    const rate = Math.round((current / previous) * 100);
    return rate > 100 ? 100 : rate;
}

/** Normaliza la respuesta RPC (objeto JSON, string serializado o wrapper). */
export function parseProductPulseResponse(raw: unknown): ProductPulseAnalytics | null {
    if (raw == null) return null;

    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }

    if (Array.isArray(payload)) {
        payload = payload[0];
    }

    if (payload && typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        const wrapped = obj.get_product_pulse_analytics;
        if (wrapped && typeof wrapped === 'object') {
            payload = wrapped;
        }
    }

    if (!payload || typeof payload !== 'object') return null;

    const p = payload as ProductPulseAnalytics;
    if (typeof p.period_days !== 'number') return null;

    return {
        ...p,
        alerts: Array.isArray(p.alerts) ? p.alerts : [],
        kpis: Array.isArray(p.kpis) ? p.kpis : [],
        feature_momentum: Array.isArray(p.feature_momentum) ? p.feature_momentum : [],
        activation_chain: p.activation_chain ?? {
            registered: 0,
            onboarding_v2_completed: 0,
            profile_completed: 0,
            first_capture: 0,
            third_capture: 0,
        },
        onboarding_insights: p.onboarding_insights ?? {
            v2_completion_rate: 0,
            worst_v2_step: null,
            worst_profile_step: null,
            location_grant_rate: null,
        },
    };
}
